import React, { useCallback, useRef } from 'react';
import { FOCUS_RING } from './focusRing';

type SegmentedControlSize = 'small' | 'medium' | 'large';

interface SegmentedControlOption<T extends string = string> {
    value: T;
    label: string;
    icon?: React.ReactNode;
    disabled?: boolean;
}

interface SegmentedControlProps<T extends string = string> {
    options: SegmentedControlOption<T>[];
    /** The selected option's value. A value not present in `options` renders nothing selected. */
    value: T;
    onChange: (value: T) => void;
    size?: SegmentedControlSize;
    /** Stretch the track to its container and split it into equal segments. */
    fullWidth?: boolean;
    /** Disables every segment. Individual segments can also be disabled via `options`. */
    disabled?: boolean;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    className?: string;
}

// The ladder governs a control's outer box. The height therefore sits on the track and
// the items fill whatever the 4px inset leaves — putting it on the item instead makes a
// `medium` segmented control 40px tall beside a 32px Dropdown.
const SIZE_CLASSES: Record<SegmentedControlSize, { text: string; track: string; item: string }> = {
    small: {
        text: 'text-[length:var(--malachite-font-size-small,13px)]',
        track: 'h-[var(--malachite-control-height-small,28px)]',
        item: 'h-full px-2.5',
    },
    medium: {
        text: 'text-[length:var(--malachite-font-size-small,13px)]',
        track: 'h-[var(--malachite-control-height-medium,32px)]',
        item: 'h-full px-2.5',
    },
    large: {
        text: 'text-[length:var(--malachite-font-size-15,15px)]',
        track: 'h-[var(--malachite-control-height-large,42px)]',
        item: 'h-full px-4',
    },
};

/** Arrow keys move AND select, per the WAI-ARIA radiogroup pattern. */
const NAV_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']);

// Deliberately no `bg-*` here: each state branch below supplies exactly one
// background-color utility. Both `bg-transparent` and `bg-[var(--…)]` are plain
// single-class utilities in the same `@layer utilities`, so an element carrying both
// resolves by stylesheet order — and the arbitrary value loses, silently rendering the
// selected thumb transparent.
const ITEM_BASE =
    'inline-flex items-center justify-center gap-1.5 rounded-[var(--malachite-radius-10,10px)] border-0 font-medium tracking-[0.13px] whitespace-nowrap transition-colors duration-150 ease-in-out';

/**
 * A rounded track of mutually-exclusive options — the segmented "one of these" control.
 *
 * Owns the track background, the selected-thumb chrome and the radiogroup semantics in
 * one place, so consumers never hand-roll a track around `PillButton` (whose `default`
 * variant tokens are tuned for standalone filter pills, not for a sunken track).
 *
 * Selection is conveyed non-visually via `role="radio"` + `aria-checked`, and by a
 * foreground-colour change as well as the thumb, so it does not rest on background
 * colour alone.
 */
const SegmentedControl = <T extends string = string>({
    options,
    value,
    onChange,
    size = 'medium',
    fullWidth = false,
    disabled = false,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    className = '',
}: SegmentedControlProps<T>) => {
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const sizeBlock = SIZE_CLASSES[size];

    const selectedIndex = options.findIndex((option) => option.value === value);
    // Roving tabindex: the group is a single tab stop. When the selected option is
    // missing or disabled, the first enabled option takes the stop so the control is
    // still reachable by keyboard.
    const selectedIsEnabled = selectedIndex >= 0 && !options[selectedIndex].disabled;
    const tabStopIndex = selectedIsEnabled
        ? selectedIndex
        : options.findIndex((option) => !option.disabled);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (disabled || !NAV_KEYS.has(event.key)) {
                return;
            }

            const enabled = options.reduce<
                Array<{ option: SegmentedControlOption<T>; index: number }>
            >((acc, option, index) => {
                if (!option.disabled) {
                    acc.push({ option, index });
                }
                return acc;
            }, []);
            if (enabled.length === 0) {
                return;
            }

            // Only once we know we'll act — otherwise Home/End etc. should still scroll.
            event.preventDefault();

            const currentPosition = enabled.findIndex(({ option }) => option.value === value);
            let nextPosition: number;
            if (event.key === 'Home') {
                nextPosition = 0;
            } else if (event.key === 'End') {
                nextPosition = enabled.length - 1;
            } else {
                const delta = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
                const from = currentPosition === -1 ? 0 : currentPosition;
                nextPosition = (from + delta + enabled.length) % enabled.length;
            }

            const next = enabled[nextPosition];
            if (next.option.value !== value) {
                onChange(next.option.value);
            }
            itemRefs.current[next.index]?.focus();
        },
        [disabled, onChange, options, value],
    );

    return (
        <div
            role="radiogroup"
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-disabled={disabled || undefined}
            onKeyDown={handleKeyDown}
            className={`${
                // One display utility, not two: `flex` and `inline-flex` are both plain
                // `display` utilities, so emitting both would resolve by stylesheet order
                // (same hazard as the background above).
                fullWidth ? 'flex w-full' : 'inline-flex'
            } ${sizeBlock.track} items-center gap-1 p-0.5 rounded-xl bg-[var(--malachite-segmented-control-track-background)] ${
                disabled ? 'opacity-50' : ''
            } ${className}`}
        >
            {options.map((option, index) => {
                const isSelected = option.value === value;
                const isDisabled = disabled || option.disabled;

                let colorClasses: string;
                if (isSelected) {
                    colorClasses =
                        'bg-[var(--malachite-segmented-control-selected-background)] text-[var(--malachite-segmented-control-selected-foreground)]';
                } else if (isDisabled) {
                    colorClasses =
                        'bg-transparent text-[var(--malachite-segmented-control-item-foreground)]';
                } else {
                    colorClasses =
                        'bg-transparent text-[var(--malachite-segmented-control-item-foreground)] hover:text-[var(--malachite-segmented-control-item-hover-foreground)] cursor-pointer';
                }

                return (
                    <button
                        key={option.value}
                        ref={(element) => {
                            itemRefs.current[index] = element;
                        }}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        disabled={isDisabled}
                        tabIndex={index === tabStopIndex ? 0 : -1}
                        onClick={() => {
                            // Radios don't toggle off on re-click.
                            if (!isDisabled && !isSelected) {
                                onChange(option.value);
                            }
                        }}
                        className={`${FOCUS_RING} ${ITEM_BASE} ${sizeBlock.text} ${sizeBlock.item} ${colorClasses} ${
                            fullWidth ? 'flex-1' : ''
                        } ${option.disabled && !disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        style={{
                            // Inline rather than a `shadow-[...]` class: Tailwind's shadow
                            // utilities compose `--tw-shadow-*` custom properties that rely on
                            // document-level @property registration and silently don't paint
                            // inside a shadow root (same caveat as `ring-*`).
                            boxShadow: isSelected
                                ? 'var(--malachite-segmented-control-selected-shadow, 0px 1px 5px 0px rgba(0, 0, 0, 0.1))'
                                : undefined,
                        }}
                    >
                        {option.icon ? (
                            <span className="inline-flex items-center justify-center w-4 h-4 shrink-0">
                                {option.icon}
                            </span>
                        ) : null}
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
};

export default SegmentedControl;
export { SegmentedControl };
export type { SegmentedControlProps, SegmentedControlOption, SegmentedControlSize };
